/**
 * FIKO PERFORMANCE MODULE
 * =======================
 * Tum performans optimizasyon dosyalari bu klasorde
 *
 * Milyonlarca CAD objesi icin muntazam 60 FPS performans
 */

// Ultra Performance Manager - Merkezi orkestrator
export {
  FikoUltraPerformanceManager,
  FikoHierarchicalSpatialIndex,
  FikoPredictiveHover,
  FikoAdaptiveQualityController,
  FikoFrameBudgetEnforcer,
  getPerformanceManager
} from './FikoUltraPerformanceManager';

export type {
  PerformanceConfig,
  QualityLevel,
  FrameMetrics,
  HoverPrediction
} from './FikoUltraPerformanceManager';

// Streaming LOD - Progressive geometry loading
export {
  FikoStreamingLODManager,
  FikoGeometryDecimator,
  FikoStreamingGeometryLoader,
  FikoProgressiveMeshViewer
} from './FikoStreamingLOD';

export type {
  StreamingGeometryChunk,
  LODLevel,
  StreamingObjectState,
  MemoryBudget
} from './FikoStreamingLOD';

// LOD Systems
export { FikoAdvancedLOD } from './FikoAdvancedLOD';
export { FikoTemporalLOD } from './FikoTemporalLOD';

// GPU Compute
export { FikoGPUComputeUltra } from './FikoGPUComputeUltra';

// GPU Memory & Rendering
export { FikoGPUHighlight } from './FikoGPUHighlight';
export { FikoGPUPicker } from './FikoGPUPicker';
export { FikoGPUSelection } from './FikoGPUSelection';
export { FikoGPUImportPipeline } from './FikoGPUImportPipeline';

// Unified Compute Pipeline
export { FikoUnifiedComputePipeline } from './FikoUnifiedComputePipeline';

// Worker Pools
export { FikoWorkerPool } from './FikoWorkerPool';

// WASM Bridge
export { FikoWasmBridge } from './FikoWasmBridge';

// Raycast
export { FikoRaycastBVH } from './FikoRaycastBVH';

// Post-Processing Effects (CAD görsel kalitesi için)
export { FikoScreenSpaceAO } from './FikoScreenSpaceAO';
export { FikoTemporalAA } from './FikoTemporalAA';
export { FikoZPrepass } from './FikoZPrepass';

// GPU Adapter - Unified WebGPU/WebGL2 Abstraction Layer
export {
    FikoGPUAdapter,
    gpuAdapter,
    isWebGPUAvailable,
    getGPUAdapterInfo
} from './FikoGPUAdapter';

export type {
    IGPUBackend,
    FikoGPUAdapterConfig,
    GPUAdapterStats
} from './FikoGPUAdapter';

// Shared Memory - Zero-copy data sharing (FAZA 3.3)
export {
    FikoSharedMemory,
    sharedMemory,
    isSharedArrayBufferAvailable,
    hasRequiredHeaders,
    shouldUseSharedMemory,
    createSharedFloat32Array,
    createSharedUint32Array,
    AtomicLock,
    AtomicCounter,
    SharedRingBuffer
} from './FikoSharedMemory';

export type {
    SharedBufferDescriptor,
    SharedGeometry,
    SharedMemoryStats
} from './FikoSharedMemory';

// HiZ Occlusion Culling - CATIA seviyesi occlusion (FAZA 4.1)
export {
    FikoHiZOcclusionCulling,
    getHiZOcclusionCulling,
    disposeHiZOcclusionCulling
} from './FikoHiZOcclusionCulling';

export type {
    HiZConfig,
    OcclusionQuery,
    OcclusionResult,
    HiZStats
} from './FikoHiZOcclusionCulling';

// View-Dependent Streaming LOD - CATIA seviyesi progressive loading (FAZA 4.2)
export {
    FikoViewDependentLOD,
    getViewDependentLOD,
    disposeViewDependentLOD
} from './FikoViewDependentLOD';

export type {
    ViewDependentConfig,
    StreamingMeshState,
    NetworkStats,
    PredictionState,
    ViewDependentStats
} from './FikoViewDependentLOD';

// Advanced Edge Detection - CATIA seviyesi edge rendering (FAZA 4.3)
export {
    FikoAdvancedEdgeDetection,
    getAdvancedEdgeDetection,
    disposeAdvancedEdgeDetection
} from './FikoAdvancedEdgeDetection';

export type {
    EdgeDetectionConfig,
    DetectedEdge,
    ClipPlane,
    EdgeRenderTarget,
    EdgeDetectionStats
} from './FikoAdvancedEdgeDetection';

// Memory Pooling Infrastructure - Zero allocation per frame (FAZA 4.4)
export {
    ObjectPool,
    Vector3Pool,
    Matrix4Pool,
    QuaternionPool,
    Box3Pool,
    RayPool,
    TypedArrayPool,
    GeometryBufferPool,
    FikoMemoryPoolManager,
    ScopedPoolAccess,
    withPoolScope,
    getMemoryPoolManager,
    disposeMemoryPoolManager,
    memoryPool
} from './FikoMemoryPooling';

export type {
    PoolConfig,
    PoolStats,
    TypedArrayPoolKey
} from './FikoMemoryPooling';

// Performance Integration - Merkezi entegrasyon katmani
export {
    FikoPerformanceIntegration,
    getPerformanceIntegration,
    disposePerformanceIntegration,
    fikoPerformance
} from './FikoPerformanceIntegration';

export type {
    PerformanceIntegrationConfig,
    PerformanceIntegrationStats
} from './FikoPerformanceIntegration';

// Unified LOD Facade - Consolidates all 4 LOD systems behind single API
export {
    FikoUnifiedLOD,
    getUnifiedLOD,
    disposeUnifiedLOD
} from './FikoUnifiedLOD';

export type {
    LODStrategy,
    UnifiedLODConfig,
    MeshRegistrationOptions,
    UnifiedLODStats
} from './FikoUnifiedLOD';

// Additional utilities
export { FikoDynamicResolutionScaler } from './FikoDynamicResolutionScaler';
export { FikoMeshCompressor } from './FikoMeshCompressor';
export { FikoInstancedOptimizer } from './FikoInstancedOptimizer';
export { FikoVirtualTextures } from './FikoVirtualTextures';
export { FikoEdgeSpatialIndex, FikoEdgeLODManager, FikoTessellatedEdgeOptimizer } from './FikoEdgeSpatialIndex';
export { FikoLazyGeometryClone, getLazyGeometryClone, isProxyGeometry } from './FikoLazyGeometryClone';
export { FikoInstancedEdgeRenderer, createInstancedEdgesFromLineSegments, createInstancedEdgesFromMesh, createInstancedEdgesFromEdgeGeometry, EdgeCurveType } from './FikoInstancedEdgeRenderer';
export type { EdgeCurveTypeValue, EdgeMetadata, EdgeFilterConfig } from './FikoInstancedEdgeRenderer';
export { FikoLargeGeometryHandler } from './FikoLargeGeometryHandler';

// Material Pool - GPU material deduplication & caching (ref-counted, LRU eviction)
export {
    FikoMaterialPool,
    materialPool,
    getMaterialPool,
    disposeMaterialPool
} from './FikoMaterialPool';

export type {
    StandardMaterialProps,
    LineBasicMaterialProps,
    LineMaterialProps,
    MaterialPoolStats
} from './FikoMaterialPool';

// Geometry Batcher - Static geometry merging for draw call reduction
export { FikoGeometryBatcher } from './FikoGeometryBatcher';
export type {
    BatchAnalysis,
    BatchGroupInfo,
    BatchOptions,
    BatchResult
} from './FikoGeometryBatcher';

// Topological Selection - Face loop, edge chain, radius group selection
export {
    FikoTopologicalSelection,
    getTopologicalSelection,
    TopolojikSecimModu,
} from './FikoTopologicalSelection';
export type {
    TopolojikSecimSonuc,
} from './FikoTopologicalSelection';
